import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ReactComponent as AddIcon } from '../../assets/images/icons/add_16.svg';
import { ReactComponent as SubtractIcon } from '../../assets/images/icons/subtract_16.svg';
import * as events from '../../lib/analytics/events';
import { Image } from '../Image';
import HardwareFirstPlanModal from '../HardwareFirstPlanModal/HardwareFirstPlanModal';
import styles from './HardwareFirstCartItem.module.scss';

export type HardwareFirstDevice = 'mini' | 'series3' | 'ultra';

interface IncludedItem {
  nameKey: string;
  descriptionKey: string;
}

/**
 * What ships in the box per device, listed under the card's "What's included?" toggle. The
 * hardware-first cart sells hardware alone, so these are contents rather than the priced bundle
 * rows the membership cart shows.
 */
const INCLUDED_ITEMS: Record<HardwareFirstDevice, IncludedItem[]> = {
  mini: [
    { nameKey: 'hardware_first_cart_mini_tracker', descriptionKey: 'hardware_first_spec_mini_battery_weeks' },
    { nameKey: 'hardware_first_cart_mini_cases', descriptionKey: 'hardware_first_cart_mini_cases_sub' },
    { nameKey: 'hardware_first_cart_charging_cable', descriptionKey: 'hardware_first_cart_charging_cable_sub' },
  ],
  series3: [
    { nameKey: 'hardware_first_cart_s3_tracker', descriptionKey: 'hardware_first_spec_s3_battery' },
    { nameKey: 'hardware_first_cart_s3_band', descriptionKey: 'hardware_first_cart_s3_band_sub' },
    { nameKey: 'hardware_first_cart_s3_base', descriptionKey: 'hardware_first_cart_s3_base_sub' },
  ],
  ultra: [
    { nameKey: 'hardware_first_cart_ultra_tracker', descriptionKey: 'hardware_first_spec_ultra_battery' },
    { nameKey: 'hardware_first_cart_charging_cable', descriptionKey: 'hardware_first_cart_charging_cable_sub' },
  ],
};

interface HardwareFirstCartItemProps {
  device: HardwareFirstDevice;
  productImageUrl: string;
  productName: string;
  /** Variant line under the name; falls back to the device's own descriptor when there is none. */
  variantDescription?: string;
  /** Absent only while a just-added row waits on the repriced total; the card renders regardless. */
  price?: string;
  /** Struck-through list price. Absent when the server did not reprice the item. */
  listPrice?: string;
  /** Formatted multi-device saving on this tracker; the banner shows while the server priced one in. */
  multiDeviceDiscount?: string;
  productUrl?: string;
  removeCartItem?: () => void;
}

/**
 * Hardware-first cart row: a card carrying the repriced hardware, the plan-at-activation line, and
 * an expandable box-contents list. Scoped to the arm — the control cart keeps `ProductCartItem`.
 */
export default function HardwareFirstCartItem({
  device,
  productImageUrl,
  productName,
  variantDescription,
  price,
  listPrice,
  multiDeviceDiscount,
  productUrl,
  removeCartItem,
}: HardwareFirstCartItemProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const includedItems = INCLUDED_ITEMS[device];

  const openPdp = productUrl
    ? () => {
        events.cartItemClicked(productUrl);
        history.push(productUrl);
      }
    : undefined;

  return (
    <div className={styles.card}>
      {multiDeviceDiscount && <div className={styles.banner}>{t('hardware_first_cart_multi_tracker_applied')}</div>}
      <div className={styles.body}>
        <div className={styles.top}>
          <div className={classNames(styles.thumb, { [styles.clickable]: !!openPdp })} onClick={openPdp}>
            <Image image={{ type: 'image', url: productImageUrl }} alt={productName} />
          </div>
          <div className={styles.meta}>
            <h3 className={classNames(styles.name, { [styles.clickable]: !!openPdp })} onClick={openPdp}>
              {productName}
            </h3>
            <p className={styles.variant}>{variantDescription ?? t('hardware_first_cart_fits_any_collar')}</p>
            <HardwareFirstPlanModal
              trigger={
                <button className={styles.planLine} type="button">
                  {t('hardware_first_cart_plan_required')}
                </button>
              }
            />
          </div>
          <div className={styles.price}>
            {price && <span className={styles.priceMain}>{price}</span>}
            {listPrice && <span className={styles.listPrice}>{listPrice}</span>}
            {removeCartItem && (
              <button className={styles.remove} onClick={removeCartItem} type="button">
                {t('remove')}
              </button>
            )}
          </div>
        </div>
        <div className={styles.included}>
          <button
            aria-expanded={expanded}
            className={styles.includedToggle}
            onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
            type="button"
          >
            <span className={styles.includedLabel}>
              {t('cart_item_v2.whats_included')}{' '}
              {t('cart_item_v2.whats_included_count', { count: includedItems.length })}
            </span>
            {expanded ? <SubtractIcon className={styles.includedIcon} /> : <AddIcon className={styles.includedIcon} />}
          </button>
          {/* always rendered: the panel animates between a zero and a content-sized grid row */}
          <div
            aria-hidden={!expanded}
            className={classNames(styles.includedPanel, { [styles.includedPanelOpen]: expanded })}
          >
            <div className={styles.includedList}>
              {includedItems.map((item) => (
                <div className={styles.includedRow} key={item.nameKey}>
                  <div className={styles.includedText}>
                    <span className={styles.includedName}>{t(item.nameKey)}</span>
                    <span className={styles.includedDescription}>{t(item.descriptionKey)}</span>
                  </div>
                  <span className={styles.includedValue}>{t('hardware_first_cart_included')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
